import React, { useState, useEffect, useCallback } from 'react';
import { getFunnelAnalytics, JOURNEY_EVENTS, FUNNEL_STAGES } from '../../../services/userJourneyService';
import { supabase } from '../../../lib/supabase';
import { TrendingDown, Eye, Play, Heart, Calendar, CheckCircle, Users, RefreshCw, MapPin, Star, ChevronDown, ChevronUp, Flame, Zap, Thermometer, Snowflake } from 'lucide-react';

// ─── Stage Config ─────────────────────────────────────────────────────────────
const STAGE_CONFIG = {
  [JOURNEY_EVENTS?.PROPERTY_VIEW]: { label: 'Vues propriété', icon: Eye, color: '#3B82F6', bg: 'rgba(59,130,246,0.1)' },
  [JOURNEY_EVENTS?.VIDEO_PLAY]: { label: 'Lectures vidéo', icon: Play, color: '#8B5CF6', bg: 'rgba(139,92,246,0.1)' },
  [JOURNEY_EVENTS?.WISHLIST_ADD]: { label: 'Ajouts favoris', icon: Heart, color: '#EC4899', bg: 'rgba(236,72,153,0.1)' },
  [JOURNEY_EVENTS?.APPOINTMENT_STARTED]: { label: 'RDV démarrés', icon: Calendar, color: '#F59E0B', bg: 'rgba(245,158,11,0.1)' },
  [JOURNEY_EVENTS?.APPOINTMENT_BOOKED]: { label: 'RDV confirmés', icon: CheckCircle, color: '#10B981', bg: 'rgba(16,185,129,0.1)' },
};

// ─── Lead Score Tiers ─────────────────────────────────────────────────────────
const SCORE_TIERS = [
  { id: 'hot', label: 'Très Chaud 🔥', minScore: 60, color: '#EF4444', bg: 'rgba(239,68,68,0.1)', icon: Flame },
  { id: 'warm', label: 'Chaud ⚡', minScore: 35, color: '#F97316', bg: 'rgba(249,115,22,0.1)', icon: Zap },
  { id: 'lukewarm', label: 'Tiède', minScore: 15, color: '#EAB308', bg: 'rgba(234,179,8,0.1)', icon: Thermometer },
  { id: 'cold', label: 'Froid', minScore: 0, color: '#3B82F6', bg: 'rgba(59,130,246,0.1)', icon: Snowflake },
];

// ─── Score a lead (mirrors adminNotificationService logic) ────────────────────
const scoreLead = (inquiry) => {
  let score = 0;
  const price = inquiry?.property?.price || 0;
  if (price >= 100_000_000) score += 40;
  else if (price >= 50_000_000) score += 25;
  else if (price >= 20_000_000) score += 10;
  if (inquiry?.phone) score += 15;
  if (inquiry?.message && inquiry?.message?.length > 50) score += 10;
  if (inquiry?.lead_source === 'roi_calculator') score += 20;
  return score;
};

const getTier = (score) => {
  if (score >= 60) return 'hot';
  if (score >= 35) return 'warm';
  if (score >= 15) return 'lukewarm';
  return 'cold';
};

// ─── Mini Funnel Bar ──────────────────────────────────────────────────────────
const FunnelBar = ({ label, value, max, color, rate, isFirst }) => {
  const pct = max > 0 ? Math.max((value / max) * 100, 2) : 2;
  const isLow = !isFirst && rate < 30;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-text-secondary w-28 flex-shrink-0 truncate">{label}</span>
      <div className="flex-1 h-7 bg-surface rounded-lg overflow-hidden relative">
        <div
          className="h-full rounded-lg transition-all duration-700 flex items-center px-2"
          style={{ width: `${pct}%`, background: color + '33', borderLeft: `3px solid ${color}` }}
        >
          <span className="text-xs font-bold" style={{ color }}>{value?.toLocaleString('fr-FR')}</span>
        </div>
      </div>
      <div className="w-16 flex-shrink-0 text-right">
        {!isFirst ? (
          <span
            className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
            style={{ color: isLow ? '#EF4444' : '#10B981', background: isLow ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)' }}
          >
            {isLow && <TrendingDown size={9} className="inline mr-0.5" />}
            {rate}%
          </span>
        ) : (
          <span className="text-xs text-text-secondary">—</span>
        )}
      </div>
    </div>
  );
};

// ─── Conversion Rate Badge ────────────────────────────────────────────────────
const ConvBadge = ({ rate, label }) => {
  const color = rate >= 50 ? '#10B981' : rate >= 25 ? '#F59E0B' : '#EF4444';
  return (
    <div className="flex flex-col items-center">
      <div
        className="text-2xl font-bold"
        style={{ color }}
      >
        {rate}%
      </div>
      <div className="text-xs text-text-secondary mt-0.5 text-center">{label}</div>
    </div>
  );
};

// ─── Commune Funnel Row ───────────────────────────────────────────────────────
const CommuneRow = ({ commune, leadToAppt, inquiryToViewing, totalLeads, totalInquiries }) => {
  const apptColor = leadToAppt >= 50 ? '#10B981' : leadToAppt >= 25 ? '#F59E0B' : '#EF4444';
  const viewColor = inquiryToViewing >= 50 ? '#10B981' : inquiryToViewing >= 25 ? '#F59E0B' : '#EF4444';
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-border last:border-0">
      <div className="flex items-center gap-1.5 w-32 flex-shrink-0">
        <MapPin size={12} className="text-brand-gold flex-shrink-0" />
        <span className="text-xs font-medium text-text-primary truncate">{commune}</span>
      </div>
      <div className="flex-1 grid grid-cols-2 gap-4">
        {/* Lead → Appointment */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-surface rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${Math.min(leadToAppt, 100)}%`, background: apptColor }}
            />
          </div>
          <span className="text-xs font-semibold w-10 text-right" style={{ color: apptColor }}>
            {leadToAppt}%
          </span>
          <span className="text-xs text-text-secondary">({totalLeads})</span>
        </div>
        {/* Inquiry → Viewing */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-surface rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${Math.min(inquiryToViewing, 100)}%`, background: viewColor }}
            />
          </div>
          <span className="text-xs font-semibold w-10 text-right" style={{ color: viewColor }}>
            {inquiryToViewing}%
          </span>
          <span className="text-xs text-text-secondary">({totalInquiries})</span>
        </div>
      </div>
    </div>
  );
};

// ─── Score Tier Funnel Row ────────────────────────────────────────────────────
const TierRow = ({ tier, leadToAppt, inquiryToViewing, totalLeads, totalInquiries }) => {
  const cfg = SCORE_TIERS?.find(t => t?.id === tier) || SCORE_TIERS?.[3];
  const TierIcon = cfg?.icon;
  const apptColor = leadToAppt >= 50 ? '#10B981' : leadToAppt >= 25 ? '#F59E0B' : '#EF4444';
  const viewColor = inquiryToViewing >= 50 ? '#10B981' : inquiryToViewing >= 25 ? '#F59E0B' : '#EF4444';
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-border last:border-0">
      <div className="flex items-center gap-1.5 w-32 flex-shrink-0">
        <TierIcon size={12} style={{ color: cfg?.color }} className="flex-shrink-0" />
        <span className="text-xs font-medium text-text-primary truncate">{cfg?.label}</span>
      </div>
      <div className="flex-1 grid grid-cols-2 gap-4">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-surface rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${Math.min(leadToAppt, 100)}%`, background: apptColor }}
            />
          </div>
          <span className="text-xs font-semibold w-10 text-right" style={{ color: apptColor }}>
            {leadToAppt}%
          </span>
          <span className="text-xs text-text-secondary">({totalLeads})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-surface rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${Math.min(inquiryToViewing, 100)}%`, background: viewColor }}
            />
          </div>
          <span className="text-xs font-semibold w-10 text-right" style={{ color: viewColor }}>
            {inquiryToViewing}%
          </span>
          <span className="text-xs text-text-secondary">({totalInquiries})</span>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ConversionFunnelDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [funnelLoading, setFunnelLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  const [communeData, setCommuneData] = useState([]);
  const [tierData, setTierData] = useState([]);
  const [globalRates, setGlobalRates] = useState({ leadToAppt: 0, inquiryToViewing: 0, totalLeads: 0, totalInquiries: 0, totalAppts: 0, totalViewings: 0 });
  const [showAllCommunes, setShowAllCommunes] = useState(false);
  const [activeView, setActiveView] = useState('commune'); // 'commune' | 'tier'

  // ── Load journey funnel ──
  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await getFunnelAnalytics(days);
    if (err) setError(err);
    else setAnalytics(data);
    setLoading(false);
  }, [days]);

  // ── Load conversion funnel data from Supabase ──
  const loadConversionData = useCallback(async () => {
    setFunnelLoading(true);
    try {
      const since = new Date();
      since?.setDate(since?.getDate() - days);
      const sinceISO = since?.toISOString();

      // Fetch inquiries with commune info
      const { data: inquiries } = await supabase
        ?.from('property_inquiries')
        ?.select(`
          id, created_at, status, message, phone, lead_source,
          property:properties(id, title, price, commune)
        `)
        ?.gte('created_at', sinceISO)
        ?.order('created_at', { ascending: false });

      // Fetch appointments with commune info
      const { data: appointments } = await supabase
        ?.from('appointments')
        ?.select(`
          id, created_at, status, appointment_type,
          property:properties(id, title, price, commune)
        `)
        ?.gte('created_at', sinceISO)
        ?.order('created_at', { ascending: false });

      const inqs = inquiries || [];
      const appts = appointments || [];

      // ── Global rates ──
      const totalLeads = inqs?.length;
      const totalAppts = appts?.filter(a => ['confirmed', 'completed', 'pending']?.includes(a?.status))?.length;
      const totalInquiries = inqs?.length;
      // "viewing" = appointments of type 'property_visit' or 'viewing'
      const totalViewings = appts?.filter(a => ['property_visit', 'viewing', 'visit']?.includes(a?.appointment_type))?.length;

      setGlobalRates({
        leadToAppt: totalLeads > 0 ? Math.round((totalAppts / totalLeads) * 100) : 0,
        inquiryToViewing: totalInquiries > 0 ? Math.round((totalViewings / totalInquiries) * 100) : 0,
        totalLeads,
        totalInquiries,
        totalAppts,
        totalViewings,
      });

      // ── By Commune ──
      const communeMap = {};
      inqs?.forEach(inq => {
        const commune = inq?.property?.commune || 'Non spécifié';
        if (!communeMap?.[commune]) communeMap[commune] = { leads: 0, inquiries: 0, appts: 0, viewings: 0 };
        communeMap[commune].leads += 1;
        communeMap[commune].inquiries += 1;
        // Score lead
        let score = scoreLead(inq);
        communeMap[commune].score = (communeMap?.[commune]?.score || 0) + score;
      });
      appts?.forEach(appt => {
        const commune = appt?.property?.commune || 'Non spécifié';
        if (!communeMap?.[commune]) communeMap[commune] = { leads: 0, inquiries: 0, appts: 0, viewings: 0 };
        if (['confirmed', 'completed', 'pending']?.includes(appt?.status)) {
          communeMap[commune].appts += 1;
        }
        if (['property_visit', 'viewing', 'visit']?.includes(appt?.appointment_type)) {
          communeMap[commune].viewings += 1;
        }
      });

      const communeRows = Object.entries(communeMap)
        ?.map(([commune, d]) => ({
          commune,
          leadToAppt: d?.leads > 0 ? Math.round((d?.appts / d?.leads) * 100) : 0,
          inquiryToViewing: d?.inquiries > 0 ? Math.round((d?.viewings / d?.inquiries) * 100) : 0,
          totalLeads: d?.leads,
          totalInquiries: d?.inquiries,
        }))
        ?.sort((a, b) => b?.totalLeads - a?.totalLeads);

      setCommuneData(communeRows);

      // ── By Lead Score Tier ──
      const tierMap = { hot: { leads: 0, inquiries: 0, appts: 0, viewings: 0 }, warm: { leads: 0, inquiries: 0, appts: 0, viewings: 0 }, lukewarm: { leads: 0, inquiries: 0, appts: 0, viewings: 0 }, cold: { leads: 0, inquiries: 0, appts: 0, viewings: 0 } };

      inqs?.forEach(inq => {
        let score = scoreLead(inq);
        const tier = getTier(score);
        tierMap[tier].leads += 1;
        tierMap[tier].inquiries += 1;
      });

      // Match appointments to inquiries by property commune to estimate tier
      // We use a simpler approach: distribute appts proportionally by tier lead count
      const totalTierLeads = Object.values(tierMap)?.reduce((s, t) => s + t?.leads, 0);
      if (totalTierLeads > 0) {
        appts?.forEach(appt => {
          // Find matching inquiry by property
          const matchingInq = inqs?.find(i => i?.property?.id === appt?.property?.id);
          const tier = matchingInq ? getTier(scoreLead(matchingInq)) : 'cold';
          if (['confirmed', 'completed', 'pending']?.includes(appt?.status)) {
            tierMap[tier].appts += 1;
          }
          if (['property_visit', 'viewing', 'visit']?.includes(appt?.appointment_type)) {
            tierMap[tier].viewings += 1;
          }
        });
      }

      const tierRows = SCORE_TIERS?.map(t => ({
        tier: t?.id,
        leadToAppt: tierMap?.[t?.id]?.leads > 0 ? Math.round((tierMap?.[t?.id]?.appts / tierMap?.[t?.id]?.leads) * 100) : 0,
        inquiryToViewing: tierMap?.[t?.id]?.inquiries > 0 ? Math.round((tierMap?.[t?.id]?.viewings / tierMap?.[t?.id]?.inquiries) * 100) : 0,
        totalLeads: tierMap?.[t?.id]?.leads,
        totalInquiries: tierMap?.[t?.id]?.inquiries,
      }));

      setTierData(tierRows);
    } catch (err) {
      console.error('Conversion funnel error:', err);
    } finally {
      setFunnelLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadAnalytics();
    loadConversionData();
  }, [loadAnalytics, loadConversionData]);

  const handleRefresh = () => {
    loadAnalytics();
    loadConversionData();
  };

  const maxCount = analytics?.funnelData?.reduce((max, s) => Math.max(max, s?.count), 1) || 1;
  const displayedCommunes = showAllCommunes ? communeData : communeData?.slice(0, 6);

  return (
    <div style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-text-primary">Entonnoir de Conversion</h2>
          <p className="text-sm text-text-secondary mt-1">Parcours lead → RDV et demande → visite, par commune et tier de score</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(Number(e?.target?.value))}
            className="text-sm border border-border rounded-lg px-3 py-2 bg-card text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-gold"
          >
            <option value={7}>7 derniers jours</option>
            <option value={30}>30 derniers jours</option>
            <option value={90}>90 derniers jours</option>
          </select>
          <button
            onClick={handleRefresh}
            disabled={loading || funnelLoading}
            className="p-2 rounded-lg border border-border bg-card hover:bg-surface transition-colors"
            title="Actualiser"
          >
            <RefreshCw size={16} className={(loading || funnelLoading) ? 'animate-spin text-brand-gold' : 'text-text-secondary'} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700 text-sm">
          Erreur de chargement : {error}
        </div>
      )}

      {/* ── Section 1: Global Conversion KPIs ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* Lead → Appointment */}
        <div className="bg-card rounded-xl border border-border p-4 col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-lg bg-emerald-50">
              <Calendar size={14} className="text-emerald-600" />
            </div>
            <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Lead → RDV</span>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold" style={{ color: globalRates?.leadToAppt >= 25 ? '#10B981' : '#EF4444' }}>
              {globalRates?.leadToAppt}%
            </span>
          </div>
          <p className="text-xs text-text-secondary mt-1">
            {globalRates?.totalAppts} RDV / {globalRates?.totalLeads} leads
          </p>
        </div>

        {/* Inquiry → Viewing */}
        <div className="bg-card rounded-xl border border-border p-4 col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-lg bg-purple-50">
              <Eye size={14} className="text-purple-600" />
            </div>
            <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Demande → Visite</span>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold" style={{ color: globalRates?.inquiryToViewing >= 25 ? '#10B981' : '#EF4444' }}>
              {globalRates?.inquiryToViewing}%
            </span>
          </div>
          <p className="text-xs text-text-secondary mt-1">
            {globalRates?.totalViewings} visites / {globalRates?.totalInquiries} demandes
          </p>
        </div>

        {/* Total Leads */}
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-lg bg-blue-50">
              <Users size={14} className="text-blue-600" />
            </div>
            <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Leads</span>
          </div>
          <div className="text-3xl font-bold text-text-primary">{globalRates?.totalLeads?.toLocaleString('fr-FR')}</div>
          <p className="text-xs text-text-secondary mt-1">sur {days} jours</p>
        </div>

        {/* Total Appointments */}
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-lg bg-amber-50">
              <CheckCircle size={14} className="text-amber-600" />
            </div>
            <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">RDV Confirmés</span>
          </div>
          <div className="text-3xl font-bold text-text-primary">{globalRates?.totalAppts?.toLocaleString('fr-FR')}</div>
          <p className="text-xs text-text-secondary mt-1">sur {days} jours</p>
        </div>
      </div>

      {/* ── Section 2: Conversion by Commune & Tier ── */}
      <div className="bg-card rounded-xl border border-border p-6 mb-6">
        {/* Tab switcher */}
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-semibold text-text-primary">Taux de conversion détaillés</h3>
          <div className="flex rounded-lg border border-border overflow-hidden text-xs">
            <button
              onClick={() => setActiveView('commune')}
              className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors ${activeView === 'commune' ? 'bg-brand-gold text-white font-semibold' : 'bg-card text-text-secondary hover:bg-surface'}`}
            >
              <MapPin size={12} />
              Par Commune
            </button>
            <button
              onClick={() => setActiveView('tier')}
              className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors ${activeView === 'tier' ? 'bg-brand-gold text-white font-semibold' : 'bg-card text-text-secondary hover:bg-surface'}`}
            >
              <Star size={12} />
              Par Score Tier
            </button>
          </div>
        </div>

        {/* Column headers */}
        <div className="flex items-center gap-3 mb-2 pb-2 border-b border-border">
          <div className="w-32 flex-shrink-0" />
          <div className="flex-1 grid grid-cols-2 gap-4">
            <div className="flex items-center gap-1.5">
              <Calendar size={12} className="text-emerald-500" />
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Lead → RDV</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Eye size={12} className="text-purple-500" />
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Demande → Visite</span>
            </div>
          </div>
        </div>

        {funnelLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-gold" />
          </div>
        ) : activeView === 'commune' ? (
          <>
            {displayedCommunes?.length > 0 ? (
              displayedCommunes?.map((row) => (
                <CommuneRow key={row?.commune} {...row} />
              ))
            ) : (
              <p className="text-xs text-text-secondary py-6 text-center">Aucune donnée de commune disponible pour cette période.</p>
            )}
            {communeData?.length > 6 && (
              <button
                onClick={() => setShowAllCommunes(v => !v)}
                className="mt-3 flex items-center gap-1 text-xs text-brand-gold hover:underline mx-auto"
              >
                {showAllCommunes ? <><ChevronUp size={12} /> Voir moins</> : <><ChevronDown size={12} /> Voir toutes les {communeData?.length} communes</>}
              </button>
            )}
          </>
        ) : (
          <>
            {tierData?.map((row) => (
              <TierRow key={row?.tier} {...row} />
            ))}
          </>
        )}
      </div>

      {/* ── Section 3: Lead Score Tier Summary Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {tierData?.map((row) => {
          const cfg = SCORE_TIERS?.find(t => t?.id === row?.tier);
          const TierIcon = cfg?.icon;
          return (
            <div key={row?.tier} className="rounded-xl border border-border p-4" style={{ background: cfg?.bg }}>
              <div className="flex items-center gap-2 mb-3">
                <TierIcon size={14} style={{ color: cfg?.color }} />
                <span className="text-xs font-semibold" style={{ color: cfg?.color }}>{cfg?.label}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-lg font-bold text-text-primary">{row?.leadToAppt}%</div>
                  <div className="text-xs text-text-secondary">Lead→RDV</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-text-primary">{row?.inquiryToViewing}%</div>
                  <div className="text-xs text-text-secondary">Demande→Visite</div>
                </div>
              </div>
              <div className="mt-2 text-xs text-text-secondary">{row?.totalLeads} leads</div>
            </div>
          );
        })}
      </div>

      {/* ── Section 4: Classic Journey Funnel ── */}
      {(loading && !analytics) ? (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-gold" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            {FUNNEL_STAGES?.map((stage) => {
              const cfg = STAGE_CONFIG?.[stage];
              const IconComp = cfg?.icon;
              const count = analytics?.eventCounts?.[stage] || 0;
              return (
                <div key={stage} className="rounded-xl border border-border p-4" style={{ background: cfg?.bg }}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 rounded-lg" style={{ background: cfg?.color + '22' }}>
                      <IconComp size={14} style={{ color: cfg?.color }} />
                    </div>
                    <span className="text-xs text-text-secondary font-medium">{cfg?.label}</span>
                  </div>
                  <div className="text-2xl font-bold text-text-primary">{count?.toLocaleString('fr-FR')}</div>
                </div>
              );
            })}
          </div>

          {/* Funnel Visualization */}
          <div className="bg-card rounded-xl border border-border p-6 mb-6">
            <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-5">
              Parcours utilisateur complet
            </h3>
            <div className="space-y-3">
              {analytics?.funnelData?.map((item, index) => {
                const cfg = STAGE_CONFIG?.[item?.stage];
                const IconComp = cfg?.icon;
                const barWidth = maxCount > 0 ? Math.max((item?.count / maxCount) * 100, 2) : 2;
                const isDropOff = index > 0 && item?.conversionRate < 30;
                return (
                  <div key={item?.stage} className="flex items-center gap-4">
                    <div className="flex items-center gap-2 w-40 flex-shrink-0">
                      <IconComp size={14} style={{ color: cfg?.color }} />
                      <span className="text-xs text-text-secondary truncate">{cfg?.label}</span>
                    </div>
                    <div className="flex-1 relative h-8 bg-surface rounded-lg overflow-hidden">
                      <div
                        className="h-full rounded-lg transition-all duration-700 flex items-center px-3"
                        style={{ width: `${barWidth}%`, background: cfg?.color + '33', borderLeft: `3px solid ${cfg?.color}` }}
                      >
                        <span className="text-xs font-bold" style={{ color: cfg?.color }}>
                          {item?.count?.toLocaleString('fr-FR')}
                        </span>
                      </div>
                    </div>
                    <div className="w-24 flex-shrink-0 text-right">
                      {index > 0 ? (
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded-full"
                          style={{
                            color: isDropOff ? '#EF4444' : '#10B981',
                            background: isDropOff ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
                          }}
                        >
                          {isDropOff && <TrendingDown size={10} className="inline mr-0.5" />}
                          {item?.conversionRate}%
                        </span>
                      ) : (
                        <span className="text-xs text-text-secondary">—</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-text-secondary mt-4">
              * Le taux de conversion indique le % d'utilisateurs passant à l'étape suivante
            </p>
          </div>

          {/* Sessions + Top Properties */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-card rounded-xl border border-border p-5">
              <div className="flex items-center gap-2 mb-4">
                <Users size={16} className="text-brand-gold" />
                <h3 className="text-sm font-semibold text-text-primary">Sessions uniques</h3>
              </div>
              <div className="text-4xl font-bold text-text-primary mb-1">
                {(analytics?.totalSessions || 0)?.toLocaleString('fr-FR')}
              </div>
              <p className="text-xs text-text-secondary">Sessions sur les {days} derniers jours</p>
            </div>

            <div className="bg-card rounded-xl border border-border p-5">
              <div className="flex items-center gap-2 mb-4">
                <Eye size={16} className="text-brand-gold" />
                <h3 className="text-sm font-semibold text-text-primary">Propriétés les plus vues</h3>
              </div>
              {analytics?.topProperties?.length > 0 ? (
                <div className="space-y-2">
                  {analytics?.topProperties?.map((item, i) => (
                    <div key={item?.propertyId} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-text-secondary w-4">{i + 1}.</span>
                        <span className="text-xs text-text-primary font-mono truncate max-w-[140px]">
                          {item?.propertyId}
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-brand-gold">{item?.views} vues</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-text-secondary">Aucune donnée disponible</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ConversionFunnelDashboard;
